import { isPreviewableType } from '@doss/common-all'
import FileSaver from 'file-saver'
import { truncate } from 'lodash'
import { X } from 'lucide-react'
import { MouseEvent, useMemo, useState } from 'react'
import { RxFile } from 'react-icons/rx'
import { cn } from '~/@/lib/utils'
import { Document } from '~/common/generated/apollo-operations'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { Chip } from './chip'

interface FileChipProps {
  className?: string
  disabled?: boolean
  document?: Pick<Document, 'id' | 'name' | 'documentType' | 'storageUrl'>
  fileName: string
  fileUrl?: string
  onDelete?: (event: MouseEvent<HTMLButtonElement, Event>) => void
}

export function FileChip({
  className,
  disabled,
  document,
  fileName,
  fileUrl,
  onDelete,
}: FileChipProps) {
  const [isDeleteHovered, setIsDeleteHovered] = useState(false)
  const { isMobile } = useDeviceType()

  const fileNameDisplay = useMemo(() => {
    const [name, extension] = fileName.split('.')

    return truncate(name, { length: 50, omission: '...' }) + '.' + extension
  }, [fileName])

  const handleClick = async (event: MouseEvent<HTMLDivElement, Event>) => {
    if (!fileUrl) {
      return
    }

    if (document && isMobile) {
      // Navigate to the location of the gcs URL when viewing on mobile.
      // The mobile app's webview has a callback which listens changes in location and can respond accordingly.
      // in the case of a GCS URL, it will download the file and present users with a share dialog to share the file to
      // the application of their choice.
      window.location.href = document.storageUrl
    } else if (document && !isPreviewableType(document.documentType)) {
      const response = await fetch(document.storageUrl)
      const blob = await response.blob()
      FileSaver.saveAs(blob, fileNameDisplay)
    } else {
      event.preventDefault()
      window.open(fileUrl, '_blank')
    }
  }

  return (
    <Chip
      className={cn(className, 'cursor-pointer group', disabled && 'bg-gray-2 text-gray-9')}
      onClick={handleClick}
      variant="gray"
    >
      <RxFile size={12} className={cn(disabled && 'text-gray-9')} />
      <span
        className={cn(
          'pointer-events-none transition-all',
          !isDeleteHovered && 'group-hover:underline',
          disabled && 'text-gray-9',
        )}
      >
        {fileNameDisplay}
      </span>
      {/* Delete button */}
      {onDelete && !disabled && (
        <button
          type="button"
          aria-label={`Remove ${fileName}`}
          className={cn(
            'pointer-events-auto transition-colors',
            isDeleteHovered ? 'text-gray-12' : '',
          )}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onDelete(e)
          }}
          onPointerOver={() => setIsDeleteHovered(true)}
          onPointerOut={() => setIsDeleteHovered(false)}
        >
          <X size={12} />
        </button>
      )}
    </Chip>
  )
}
